import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Zap,
  BookMarked,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Info,
  RefreshCw,
} from 'lucide-react';
import { BrandIcons } from '../components/ui/brand-icons';

export default function XBookmarksDocsPage() {
  const { t } = useTranslation('integrations');

  const scopes = t('xBookmarksDocs.scopes.list', { returnObjects: true }) as string[];
  const troubleshooting = t('xBookmarksDocs.troubleshooting.items', {
    returnObjects: true,
  }) as Array<{ title: string; description: string }>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
              <BrandIcons.twitter className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('xBookmarksDocs.title')}</h1>
              <p className="text-gray-600">{t('xBookmarksDocs.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <Badge variant="default" className="bg-green-100 text-green-800">
              {t('xBookmarksDocs.badgeReady')}
            </Badge>
            <span className="text-sm text-gray-500">{t('xBookmarksDocs.setupTime')}</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-sky-500" />
              {t('xBookmarksDocs.quickStart.title')}
            </CardTitle>
            <CardDescription>{t('xBookmarksDocs.quickStart.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-blue-900">{t('xBookmarksDocs.prerequisites.title')}</h4>
              </div>
              <p className="text-blue-800 text-sm">{t('xBookmarksDocs.prerequisites.body')}</p>
            </div>

            <ol className="space-y-4 list-none p-0 m-0">
              {(
                t('xBookmarksDocs.quickStart.stepsList', {
                  returnObjects: true,
                }) as Array<{ title: string; body: string }>
              ).map((step, index) => (
                <li key={step.title} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-sky-600">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-2">{step.title}</h4>
                    <p className="text-gray-600 text-sm">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>

            <Button asChild className="w-full">
              <Link to="/dashboard?view=integrations">
                {t('xBookmarksDocs.ctaConnect')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookMarked className="w-5 h-5 text-sky-500" />
              {t('xBookmarksDocs.whatIsImported.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-600">
            <p>{t('xBookmarksDocs.whatIsImported.body')}</p>
            <p>
              <Link to="/roadmap/image-memories#image-interest" className="font-medium text-sky-700 underline">
                {t('memoryModalities.imagesEarlyAccess')}
              </Link>
            </p>
            <ul className="list-disc pl-5 space-y-1">
              {(t('xBookmarksDocs.whatIsImported.bullets', { returnObjects: true }) as string[]).map(
                (item) => (
                  <li key={item}>{item}</li>
                )
              )}
            </ul>
            <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
              {t('xBookmarksDocs.whatIsImported.unbookmarkNote')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-sky-500" />
              {t('xBookmarksDocs.sync.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-600 space-y-2">
            <p>{t('xBookmarksDocs.sync.body')}</p>
            <p>{t('xBookmarksDocs.sync.schedulerNote')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('xBookmarksDocs.scopes.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="font-mono text-sm space-y-1 text-gray-700">
              {Array.isArray(scopes) &&
                scopes.map((scope) => (
                  <li key={scope}>
                    <code className="bg-gray-100 px-1 rounded">{scope}</code>
                  </li>
                ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              {t('xBookmarksDocs.troubleshooting.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Array.isArray(troubleshooting) &&
              troubleshooting.map((item) => (
                <div key={item.title}>
                  <h4 className="font-medium text-gray-900">{item.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                </div>
              ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('xBookmarksDocs.related.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" asChild>
              <Link to="/integrations/x-bookmarks">{t('xBookmarksDocs.related.landing')}</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/blog/x-bookmarks-integration-launch">{t('xBookmarksDocs.related.blog')}</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/docs">{t('xBookmarksDocs.related.docsHub')}</Link>
            </Button>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-4 bg-white rounded-lg border">
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
            <div>
              <h4 className="font-medium text-gray-900">{t('xBookmarksDocs.highlights.search.title')}</h4>
              <p className="text-sm text-gray-600">{t('xBookmarksDocs.highlights.search.body')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-white rounded-lg border">
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
            <div>
              <h4 className="font-medium text-gray-900">{t('xBookmarksDocs.highlights.agents.title')}</h4>
              <p className="text-sm text-gray-600">{t('xBookmarksDocs.highlights.agents.body')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
